import _ from "lodash";
// Use our wrapper that works with moduleResolution: "node"
// import { configureMeasurements, allMeasures } from '../../../utils/convert-units-wrapper';

import { useStore } from '../providers/AppProvider';
import { dcRound } from './misc';
import configureMeasurements from 'convert-units';
import allMeasures from 'convert-units/definitions/all';

const convert = configureMeasurements(allMeasures);
console.log('configureMeasurements', configureMeasurements);
// console.log('allMeasures', allMeasures);

/* Example localePreferences object
{
        "date": {
            "timezone": "UTC",
            "dateFormat": "YYYY-MM-DD",
            "timeFormat": "HH:mm:ss"
        },
        "money": {
            "currency": "USD",
            "decimalSeparator": ".",
            "thousandsSeparator": ","
        },
        "locale": "en-US",
        "measureUnits": {
            "area": {
                "mid": {
                    "abbr": "ac",
                    "measure": "area",
                    "system": "imperial",
                    "singular": "Acre",
                    "plural": "Acres"
                },
                "large": {
                    "abbr": "mi2",
                    "measure": "area",
                    "system": "imperial",
                    "singular": "Square Mile",
                    "plural": "Square Miles"
                },
                "small": {
                    "abbr": "ft2",
                    "measure": "area",
                    "system": "imperial",
                    "singular": "Square Foot",
                    "plural": "Square Feet"
                }
            },
            "length": {
                "mid": {
                    "abbr": "ft",
                    "measure": "length",
                    "system": "imperial",
                    "singular": "Foot",
                    "plural": "Feet"
                },
                "large": {
                    "abbr": "mi",
                    "measure": "length",
                    "system": "imperial",
                    "singular": "Mile",
                    "plural": "Miles"
                },
                "small": {
                    "abbr": "in",
                    "measure": "length",
                    "system": "imperial",
                    "singular": "Inch",
                    "plural": "Inches"
                }
            },
            "volume": {
                "mid": "q",
                "large": {
                    "abbr": "gal",
                    "measure": "volume",
                    "system": "imperial",
                    "singular": "Gallon",
                    "plural": "Gallons"
                },
                "small": {
                    "abbr": "fl-oz",
                    "measure": "volume",
                    "system": "imperial",
                    "singular": "Fluid Ounce",
                    "plural": "Fluid Ounces"
                }
            },
            "temperature": {
                "units": "f"
            }
        }
        */

// const convert = configureMeasurements && allMeasures ? configureMeasurements(allMeasures) : null;

/* --- minimal helpers for cubic handling (no signature changes anywhere) --- */
const isCubicUnit = (u?: string) => typeof u === 'string' && u.endsWith('3');
const stripCubic = (u: string) => u.slice(0, -1); // 'm3' -> 'm'
/* ------------------------------------------------------------------------- */

/**
 * Converts a value from one unit to another using the configured measurements.
 *
 * @param value - The numeric value to convert.
 * @param fromUnit - The unit abbreviation to convert from (e.g., 'ft', 'm').
 * @param toUnit - The unit abbreviation to convert to (e.g., 'm', 'ft').
 * @returns The converted value, or the original value if conversion fails.
 * @throws Error if any parameter is invalid.
 */
export const unitsConvert = (value: number | undefined, fromUnit: string, toUnit: string) => {
  // If value is undefined or null, return undefined immediately to avoid crashing
  if (value === undefined || value === null) {
    return undefined;
  }

  //console.log("C081925 unitsConvert", { value, fromUnit, toUnit });
  const badValue = Number.isNaN(value as number);
  const badFrom  = !fromUnit || typeof fromUnit !== 'string';
  const badTo    = !toUnit   || typeof toUnit   !== 'string';

  if (badValue || badFrom || badTo) {
    throw new Error('Invalid parameters for convertUnits');
  }

  if (!convert) {
    console.warn('Convert-units not properly initialized, returning original value');
    return value;
  }

  try {
    return convert(value).from(fromUnit).to(toUnit);
  } catch (error) {
    console.error('Error converting units:', error, { value, fromUnit, toUnit });
    return value; // fail soft
  }
};


// TODO: This really needs to be a map of the various options and how they should actually be displayed
/**
 * Formats a unit abbreviation for display purposes.
 *
 * @param unitAbbrev - The unit abbreviation to format (e.g., 'ft2', 'm2').
 * @returns The formatted unit abbreviation string (e.g., 'sq ft', 'sq m').
 */
export const formatUnitAbbreviation = (unitAbbrev: string) => {
  let formattedUnitAbbrev = unitAbbrev;

  switch (unitAbbrev) {
    case 'ft2':
      formattedUnitAbbrev = 'sq ft';
      break;
    case 'm2':
      formattedUnitAbbrev = 'sq m';
      break;
    case 'mi2':
      formattedUnitAbbrev = 'sq mi';
      break;
    case 'km2':
      formattedUnitAbbrev = 'sq km';
      break;
    case 'yd2':
      formattedUnitAbbrev = 'sq yd';
      break;

    /* minimal additions for cubic display */
    case 'ft3':
      formattedUnitAbbrev = 'cu ft';
      break;
    case 'm3':
      formattedUnitAbbrev = 'cu m';
      break;
  }

  return formattedUnitAbbrev;
}

/**
 * Converts a value to the user's preferred unit based on locale preferences.
 *
 * @param value - The numeric value to convert.
 * @param fromUnit - The unit abbreviation to convert from.
 * @param unitType - The type of unit (e.g., 'area', 'length').
 * @param unitScale - The scale of the unit (e.g., 'mid', 'large', 'small').
 * @param localePreferences - The locale preferences object containing unit settings.
 * @returns The converted value in the preferred unit, or null if not available.
 */
export const convertToPreferredUnitValue = (value: number, fromUnit: string, unitType: string, unitScale: string, localePreferences: any) => {
  // console.log({ localePreferences });
  const preferredUnit = _.get(localePreferences, `measureUnits.${unitType}.${unitScale}`, null);
  // console.log({ value, fromUnit,unitType, unitScale, preferredUnit });
  if (!preferredUnit) {
    return null;
  }

  // If input is cubic (e.g., 'm3' or 'ft3'), treat it as "length ^ 3":
  // - use the preferred *length* abbr (e.g., 'ft')
  // - compute length factor, then cube it and apply to value
  /*if (isCubicUnit(fromUnit)) {
    const baseFrom = stripCubic(fromUnit); // 'm3' -> 'm'
    const toAbbrLinear = preferredUnit.abbr || preferredUnit; // e.g., 'ft'
    try {
      const lengthFactor = unitsConvert(1, baseFrom, toAbbrLinear) || 0; // m -> ft
      return value * lengthFactor * lengthFactor * lengthFactor;    // m^3 -> ft^3
    } catch (e) {
      console.error('Error converting cubic units via length factor:', e, { value, fromUnit, unitType, unitScale, toAbbrLinear });
      return value; // fail soft
    }
  }*/

  return unitsConvert(value, fromUnit, preferredUnit.abbr || preferredUnit);
};


/**
 * Converts a value to the user's preferred unit and returns a formatted string.
 *
 * @param fromValue - The numeric value to convert.
 * @param fromUnit - The unit abbreviation to convert from.
 * @param unitType - The type of unit (e.g., 'area', 'length').
 * @param unitScale - The scale of the unit (e.g., 'mid', 'large', 'small').
 * @param unitStringType - The type of unit string to display ('abbr' or 'name'). Defaults to 'abbr'.
 * @param localePreferences - The locale preferences object containing unit settings.
 * @param roundingOverride - Optional override for rounding behavior { override: [null,0], currency: false }.
 * @returns A formatted string with the converted value and unit.
 */
export const convertToPreferredUnitString = (fromValue: number, fromUnit: string, unitType: string, unitScale: string, unitStringType = 'abbr', localePreferences?: any, roundingOverride?: any) => {
  const convertedValue = convertToPreferredUnitValue(fromValue, fromUnit, unitType, unitScale, localePreferences);
  if (convertedValue === null || convertedValue === undefined) {
    return '?? ??';
  }
  const uType = _.get(localePreferences, `measureUnits.${unitType}`);
  //console.log({ uType });
  let unit = _.get(localePreferences, `measureUnits.${unitType}.${unitScale}.abbr`, '');

  // For cubic inputs, show the preferred length abbr with a '3' suffix (e.g., 'ft3')
  if (isCubicUnit(fromUnit) && typeof unit === 'string' && unit && !unit.endsWith('3')) {
    unit = `${unit}3`;
  }

  let unitString = '';
  if (unitStringType === 'abbr') {
    unitString = ` ${formatUnitAbbreviation(unit)}`;
  } else if (unitStringType === 'name') {
    unitString = ` ${getPreferredUnitName(fromValue, fromUnit, unitType, unitScale, localePreferences)}`;
  }
  /*console.log("080825 fromValue", fromValue);
  console.log("080825 fromUnit", fromUnit);
  console.log("080825 unitType", unitType);
  console.log("080825 unitScale", unitScale);
  console.log("080825 unitStringType", unitStringType);
  console.log("080825 localePreferences", localePreferences);
  console.log("080825 convertedValue", convertedValue);
  console.log("080825 uType", uType);
  console.log("080825 unit", unit);*/
  // TODO: Implement dcRound() here somehow
  //return `${convertedValue.toFixed(2)} ${unitString}`;
  return `${dcRound(convertedValue, undefined, roundingOverride && roundingOverride.override[0] ? roundingOverride.override : undefined, true)} ${unitString}`;
};

/**
 * Gets the preferred unit name (singular or plural) for a value based on locale preferences.
 *
 * @param fromValue - The numeric value to convert.
 * @param fromUnit - The unit abbreviation to convert from.
 * @param unitType - The type of unit (e.g., 'area', 'length').
 * @param unitScale - The scale of the unit (e.g., 'mid', 'large', 'small').
 * @param localePreferences - The locale preferences object containing unit settings.
 * @returns The preferred unit name (singular or plural) as a string.
 */
export const getPreferredUnitName = (fromValue: number, fromUnit: string, unitType: string, unitScale: string, localePreferences: any) => {
  const convertedValue = convertToPreferredUnitValue(fromValue, fromUnit, unitType, unitScale, localePreferences);
  if (convertedValue === null || convertedValue === undefined) {
    return '?? ??';
  }
  if (fromValue === 1) {
    return _.get(localePreferences, `measureUnits.${unitType}.${unitScale}.singular`, '??');
  }

  return _.get(localePreferences, `measureUnits.${unitType}.${unitScale}.plural`, '??');
};

// Custom hook for React components that need unit conversion with locale preferences
/**
 * Custom React hook for unit conversion using locale preferences from the store.
 *
 * @returns An object containing utility functions for unit conversion:
 *   - convertToPreferredUnitValue: Converts a value to the preferred unit.
 *   - convertToPreferredUnitString: Converts a value and returns a formatted string.
 *   - getPreferredUnitName: Gets the preferred unit name (singular/plural).
 *   - unitsConvert: Direct unit conversion function.
 */
export const useUnitConversion = () => {
  const { localePreferences } = useStore();
  return {
    convertToPreferredUnitValue: (
      value: number,
      fromUnit: string,
      unitType: string,
      unitScale: string
    ) => convertToPreferredUnitValue(value, fromUnit, unitType, unitScale, localePreferences),

    convertToPreferredUnitString: (
      value: number,
      fromUnit: string,
      unitType: string,
      unitScale: string,
      unitStringType: string = 'abbr',
      roundingOverride?: any
    ) => convertToPreferredUnitString(value, fromUnit, unitType, unitScale, unitStringType, localePreferences, roundingOverride),

    getPreferredUnitName: (
      fromValue: number,
      fromUnit: string,
      unitType: string,
      unitScale: string
    ) => getPreferredUnitName(fromValue, fromUnit, unitType, unitScale, localePreferences),

    unitsConvert
  };
};
